import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { ReferralSummary } from '../../api/client';
import { C } from '../ChatAssistant/compact/tokens';
import { font as f } from '../ChatAssistant/compact/style';
import { Icon } from '../ChatAssistant/compact/kit';
import { dmy, usdCents } from './model';
import { GhostBtn, Head, StatStrip, THeadRow, Table } from './ui';
import { ROW_LINE } from './styles';
import { LoadingBlock, BusyLabel } from '../Spinner';

const TRC20 = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;
const HIST_COLS = 'minmax(120px,1fr) minmax(160px,1.4fr) 100px 80px';
const pct = (bps: number) => String(Number((bps / 100).toFixed(2))) + '%';

export function ReferralTab({ setError }: { setError: (m: string) => void }) {
  const { t } = useTranslation('paymentsCompact');
  const [data, setData] = useState<ReferralSummary | null>(null);
  const [copied, setCopied] = useState(false);
  const [armed, setArmed] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [wal, setWal] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    api.getReferralSummary()
      .then((d) => { if (alive) { setData(d); setWal(d.payout_wallet_address || ''); } })
      .catch((e) => { if (alive) setError(e instanceof Error ? e.message : t('errors.load')); });
    return () => { alive = false; };
  }, [setError, t]);

  if (!data) return <LoadingBlock pad="24px 0" size={20} label={t('loading')} />;

  const e = data.economics, earn = data.earnings;
  const saved = data.payout_wallet_address || '';
  const walTrim = wal.trim();
  const walValid = TRC20.test(walTrim);
  const dirty = walTrim !== saved;
  const canSave = dirty && (walValid || walTrim === '') && !saving;

  const copy = () => {
    navigator.clipboard.writeText(data.code).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const regen = async () => {
    if (!armed) { setArmed(true); return; }
    setRegenerating(true);
    try {
      const r = await api.regenerateReferralCode();
      setData((d) => d && { ...d, code: r.code });
      setCopied(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.regenerate'));
    } finally {
      setRegenerating(false);
      setArmed(false);
    }
  };

  const saveWallet = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      const r = await api.updateReferralWallet(walTrim || null);
      setData((d) => d && { ...d, payout_wallet_address: r.payout_wallet_address });
      setWal(r.payout_wallet_address || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('errors.wallet'));
    } finally {
      setSaving(false);
    }
  };

  const ladder = e.ladder;
  const curI = ladder.reduce((c, tier, i) => (e.paying_account_count >= tier.min_count ? i : c), 0);
  const next = ladder[curI + 1];

  const card = { background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, padding: '14px 16px', display: 'flex', flexDirection: 'column' as const, gap: 10 };

  return (
    <>
      <span style={{ font: f(400, 12), color: C.sub }}>{t('ref.intro')}</span>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(min(100%,360px),1fr))', gap: 12 }}>
        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="gift" size={14} color={C.purpleLt} />
            <span style={{ font: f(600, 13), color: C.text }}>{t('ref.code')}</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ font: '700 24px var(--font-mono)', letterSpacing: '.12em', color: C.purplePale }}>{data.code}</span>
            <GhostBtn icon={copied ? 'check' : 'copy'} height={28} onClick={copy}>{copied ? t('copied') : t('copy')}</GhostBtn>
            <GhostBtn icon="refresh-cw" height={28} onClick={() => void regen()} disabled={regenerating} title={t('ref.regenTip')} style={armed ? { borderColor: C.purple, color: C.purplePale } : undefined}>
              {regenerating ? t('ref.regenerating') : armed ? t('ref.regenConfirm') : t('ref.regen')}
            </GhostBtn>
          </div>
          <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ref.regenTip')}</span>
        </div>

        <div style={card}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Icon name="credit-card" size={14} color={C.purpleLt} />
            <span style={{ font: f(600, 13), color: C.text }}>{t('ref.payout')}</span>
            <span style={{ font: f(400, 11.5), color: C.muted }}>{t('ref.payoutNet')}</span>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={wal}
              onChange={(ev) => setWal(ev.target.value)}
              onKeyDown={(ev) => { if (ev.key === 'Enter') void saveWallet(); }}
              spellCheck={false}
              placeholder="T…"
              style={{ flex: 1, minWidth: 0, height: 32, boxSizing: 'border-box', padding: '0 10px', background: C.input, border: `1px solid ${C.borderHi}`, borderRadius: 6, color: C.text, font: '400 12.5px var(--font-mono)', outline: 'none' }}
            />
            <button type="button" onClick={() => void saveWallet()} disabled={!canSave} className="pay-primary" style={{ height: 32, padding: '0 14px', border: 0, borderRadius: 6, background: C.purple, color: '#fff', font: f(600, 12.5), cursor: canSave ? 'pointer' : 'default', whiteSpace: 'nowrap', opacity: canSave ? 1 : 0.45 }}>
              <BusyLabel busy={saving} label={t('saving')}>{saved && !dirty ? t('saved') : t('save')}</BusyLabel>
            </button>
          </div>
          <span style={{ display: 'flex', alignItems: 'flex-start', gap: 6, font: f(400, 11.5, 1.4), color: C.purplePale }}>
            <Icon name="alert-circle" size={13} style={{ marginTop: 1 }} />
            {walTrim && !walValid ? t('ref.walletBad') : !saved ? t('ref.walletMissing') : t('ref.walletNote')}
          </span>
        </div>
      </div>

      <StatStrip
        min={150}
        items={[
          { label: t('ref.stats.unpaid'), value: usdCents(earn.unpaid_balance_cents), sub: t('ref.stats.unpaidSub'), accent: true },
          { label: t('ref.stats.total'), value: usdCents(earn.total_accrued_cents), sub: t('ref.stats.totalSub') },
          { label: t('ref.stats.paid'), value: usdCents(earn.paid_out_cents), sub: t('ref.stats.paidSub') },
          { label: t('ref.stats.users'), value: String(earn.referred_user_count), sub: t('ref.stats.usersSub') },
          { label: t('ref.stats.paying'), value: String(earn.paying_account_count), sub: t('ref.stats.payingSub') },
        ]}
      />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Head title={t('ref.terms')} hint={t('ref.termsHint')} />
        <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 8, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {[
              { v: pct(e.current_rate_bps), label: t('ref.rate'), sub: t('ref.rateSub'), accent: true },
              { v: pct(e.discount_bps), label: t('ref.bonus'), sub: t('ref.bonusSub') },
              { v: t('ref.days', { count: e.window_days }), label: t('ref.window'), sub: t('ref.windowSub') },
            ].map((x) => (
              <div key={x.label} style={{ flex: '1 1 220px', display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: ROW_LINE }}>
                <span style={{ font: f(700, 18), color: x.accent ? C.purplePale : C.text, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{x.v}</span>
                <span style={{ display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 }}>
                  <span style={{ font: f(500, 12.5), color: C.text }}>{x.label}</span>
                  <span style={{ font: f(400, 11.5), color: C.muted }}>{x.sub}</span>
                </span>
              </div>
            ))}
          </div>
          <div style={{ padding: '12px 16px 14px', display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ font: f(600, 12.5), color: C.text }}>{t('ref.ladder')}</span>
              <span style={{ font: f(400, 11.5), color: C.sub }}>
                {next ? t('ref.ladderNext', { count: next.min_count - e.paying_account_count, rate: pct(next.rate_bps) }) : t('ref.ladderTop')}
              </span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${Math.max(1, ladder.length)},minmax(0,1fr))`, gap: 4 }}>
              {ladder.map((tier, i) => (
                <div key={tier.min_count} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ height: 6, borderRadius: 99, background: i <= curI ? C.purple : C.border }} />
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 1, paddingRight: 8 }}>
                    <span style={{ font: f(700, 14), color: i === curI ? C.purplePale : C.text }}>{pct(tier.rate_bps)}</span>
                    <span style={{ font: f(500, 12), color: i === curI ? C.text : C.sub }}>{t('ref.paying', { count: tier.min_count })}</span>
                  </div>
                  {i === curI && (
                    <span style={{ display: 'flex', alignItems: 'center', gap: 4, font: f(500, 11), color: C.purpleLt }}>
                      <Icon name="check" size={11} />
                      {t('ref.here')}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <Head title={t('ref.history')} hint={t('ref.entries', { count: data.accruals.length })} />
        <Table minWidth={480}>
          <THeadRow cols={HIST_COLS}>
            <span>{t('ref.col.commission')}</span>
            <span>{t('ref.col.from')}</span>
            <span>{t('ref.col.date')}</span>
            <span style={{ textAlign: 'right' }}>{t('ref.col.status')}</span>
          </THeadRow>
          {data.accruals.length === 0 ? (
            <div style={{ padding: '24px 14px', font: f(400, 12.5), color: C.muted }}>{t('ref.empty')}</div>
          ) : data.accruals.map((a, i) => (
            <div key={a.id} style={{ display: 'grid', gridTemplateColumns: HIST_COLS, gap: 12, alignItems: 'center', height: 42, padding: '0 14px', borderTop: i ? ROW_LINE : 0 }}>
              <span style={{ font: f(600, 13), color: C.text, fontVariantNumeric: 'tabular-nums' }}>{usdCents(a.commission_cents)}</span>
              <span style={{ font: f(400, 12.5), color: C.sub }}>{t('ref.from', { rate: pct(a.rate_bps), amount: usdCents(a.amount_charged_cents) })}</span>
              <span style={{ font: f(400, 12), color: C.sub, fontVariantNumeric: 'tabular-nums' }}>{dmy(a.created_at)}</span>
              <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <span style={{ height: 20, padding: '0 8px', display: 'flex', alignItems: 'center', borderRadius: 99, background: a.paid ? 'var(--om-hover)' : C.tint16, color: a.paid ? C.sub : C.purplePale, font: f(500, 11.5) }}>
                  {a.paid ? t('ref.paid') : t('ref.unpaid')}
                </span>
              </span>
            </div>
          ))}
        </Table>
      </div>
    </>
  );
}
